import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const WEEK_DAYS = [
    
    { id: 1, label: "Mon" },
    { id: 2, label: "Tue" },
    { id: 3, label: "Wed" },
    { id: 4, label: "Thu" },
    { id: 5, label: "Fri" },
    { id: 6, label: "Sat" },
    { id: 0, label: "Sun" }
];

export default function SiteSmartChargingWizard({ onSubmit, initialSchedule }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // New: first choice (flow)
    // siteMax = "bara site max (all time)"
    // daily / weekly = schema
    const [flow, setFlow] = useState("none"); // siteMax | daily | weekly | none

    // Site max power (ChargingSiteCapacityDto.MaxPowerW)
    const [siteMaxKw, setSiteMaxKw] = useState("");

    // Schedule power (ChargingSitePowerScheduleDto.MaxPowerW)
    const [scheduleMaxKw, setScheduleMaxKw] = useState("");
    
    // Validity
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Weekly days
    const [recurrenceDays, setRecurrenceDays] = useState([]);

    // Time window
    const [timeOfDayStart, setTimeOfDayStart] = useState("");
    const [timeOfDayEnd, setTimeOfDayEnd] = useState("");

    // Edit id
    const [existingChargingSitePowerSchedulePublicId, setExistingChargingSitePowerSchedulePublicId] =
        useState(null);

    const isSiteMaxOnly = flow === "siteMax";
    const isDaily = flow === "daily";
    const isWeekly = flow === "weekly";

    const steps = useMemo(() => {
        // Step 0 is always "Type"
        if (isSiteMaxOnly) return ["Type", "Max power", "Summary"];
        if (isDaily) return ["Type", "Validity", "Time window", "Schedule power", "Summary"];
        if (isWeekly) return ["Type", "Validity", "Days", "Time window", "Schedule power", "Summary"];
        return ["Type"];
    }, [isSiteMaxOnly, isDaily, isWeekly]);
    const stepLabel = {
        Type: t("smartCharging.type"),
        "Max power": t("smartCharging.maxPower"),
        Summary: t("common.summary"),
        Validity: t("smartCharging.validity"),
        "Time window": t("smartCharging.timeWindow"),
        "Schedule power": t("smartCharging.schedulePower"),
        Days: t("smartCharging.days"),
    };

    const reset = () => {
        setStep(0);
        setIsSubmitting(false);

        setExistingChargingSitePowerSchedulePublicId(null);

        setFlow("none");

        setSiteMaxKw("");
        setScheduleMaxKw("");

        setStartDate("");
        setEndDate("");

        setRecurrenceDays([]);

        setTimeOfDayStart("");
        setTimeOfDayEnd("");
    };

    useEffect(() => {
        if (!initialSchedule) {
            reset();
            return;
        }

        setExistingChargingSitePowerSchedulePublicId(
            initialSchedule.existingChargingSitePowerSchedulePublicId ?? null
        );

        setSiteMaxKw(initialSchedule.siteMaxKw ?? "");
        setScheduleMaxKw(initialSchedule.scheduleMaxKw ?? "");

        const rk = (initialSchedule.recurrencyKind ?? "").toLowerCase();
        if (!rk || rk === "none") setFlow("siteMax");
        else if (rk === "daily") setFlow("daily");
        else if (rk === "weekly") setFlow("weekly");
        else setFlow("daily");

        setStartDate(initialSchedule.startDate ?? "");
        setEndDate(initialSchedule.endDate ?? "");

        setRecurrenceDays(Array.isArray(initialSchedule.recurrenceDays) ? initialSchedule.recurrenceDays : []);

        setTimeOfDayStart(initialSchedule.timeOfDayStart ?? "");
        setTimeOfDayEnd(initialSchedule.timeOfDayEnd ?? "");

        setIsSubmitting(false);

        const isEdit = !!initialSchedule.existingChargingSitePowerSchedulePublicId;
        setStep(isEdit ? 1 : 0);
    }, [initialSchedule]);


    const nextStep = () => setStep((s) => Math.min(s + 1, steps.length - 1));
    const prevStep = () => setStep((s) => Math.max(s - 1, 0));

    // Step indexes
    const STEP_TYPE = 0;
    const STEP_SITE_MAX = isSiteMaxOnly ? 1 : -1;
    const STEP_VALIDITY = (isDaily || isWeekly) ? 1 : -1;
    const STEP_DAYS = isWeekly ? 2 : -1;
    const STEP_TIME = isDaily ? 2 : isWeekly ? 3 : -1;
    const STEP_SCHEDULE_POWER = isDaily ? 3 : isWeekly ? 4 : -1;
    const STEP_SUMMARY = steps.length - 1;

    // ---------- validations ----------
    const canGoNextFromType = flow !== "none";

    const siteIsEmpty = String(siteMaxKw).trim() === "";
    const siteInvalid = !siteIsEmpty && Number(siteMaxKw) <= 0;

    // Om siteMax-flow: du får gå vidare även om tomt (för att rensa)
    const canGoNextFromSite = isSiteMaxOnly ? !siteInvalid : (!siteIsEmpty && !siteInvalid);

    const hasDates = !!startDate && !!endDate;
    const dateOrderInvalid = hasDates && new Date(startDate) > new Date(endDate);
    const canGoNextFromValidity = hasDates && !dateOrderInvalid;

    const weeklyValid = !isWeekly || recurrenceDays.length > 0;

    const timeSomethingFilled = !!timeOfDayStart || !!timeOfDayEnd;
    const timeWindowInvalid =
        timeSomethingFilled && (!timeOfDayStart || !timeOfDayEnd || timeOfDayEnd <= timeOfDayStart);
    const canGoNextFromTimeWindow = !!timeOfDayStart && !!timeOfDayEnd && !timeWindowInvalid;

    const scheduleHasValue = String(scheduleMaxKw).trim() !== "";
    const scheduleInvalid = scheduleHasValue && Number(scheduleMaxKw) <= 0;
    const canGoNextFromSchedulePower = scheduleHasValue && !scheduleInvalid;

    const toggleWeekDay = (id) => {
        setRecurrenceDays((prev) => {
            const set = new Set(prev);
            if (set.has(id)) set.delete(id);
            else set.add(id);
            return Array.from(set).sort((a, b) => a - b);
        });
    };

    const summaryRecurrence = () => {
        if (isSiteMaxOnly) return t("common.allTime");
        if (isDaily) return t("smartCharging.daily");
        return t("smartCharging.weeklyDays", {
            days: recurrenceDays
                .slice()
                .sort((a, b) => a - b)
                .map((d) => t(`weekdayShort.${d}`))
                .join(", "),
        });
    };

    const handleSubmit = () => {
        if (isSubmitting) return;

        const recurrencyKind = isSiteMaxOnly ? null : isDaily ? "DAILY" : "WEEKLY";

        const payload = {
            // Site max only: this is the "max power"
            // For daily/weekly: we still send siteMaxKw (often used outside window) if you have it.
            siteMaxKw,

            // Schedule max
            scheduleMaxKw: isSiteMaxOnly ? siteMaxKw : scheduleMaxKw,

            startDate: isSiteMaxOnly ? null : startDate,
            endDate: isSiteMaxOnly ? null : endDate,

            recurrencyKind,
            recurrenceDays: isWeekly ? recurrenceDays : [],

            timeOfDayStart: isSiteMaxOnly ? null : timeOfDayStart,
            timeOfDayEnd: isSiteMaxOnly ? null : timeOfDayEnd,

            existingChargingSitePowerSchedulePublicId,
        };

        onSubmit({ ...payload, flow });
    };

    return (
        <div className="w-full p-2 sm:p-4 md:p-6 space-y-8">
            {/* Step indicators */}
            <div className="flex items-center justify-between mb-4">
                {steps.map((label, i) => (
                    <div key={label} className="flex-1 text-center">
                        <div
                            className={
                                "w-8 h-8 mx-auto rounded-full flex items-center justify-center font-semibold " +
                                (step === i ? "bg-blue-500 text-white" : "bg-mute-200 text-mute-600")
                            }
                        >
                            {i + 1}
                        </div>
                        <div className="mt-1 text-xs font-semibold text-mute-700">{stepLabel[label] ?? label}</div>
                    </div>
                ))}
            </div>

            <hr className="border-mute-300" />

            {/* STEP 0 – Type */}
            {step === STEP_TYPE && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("smartCharging.configureWhat")}</h2>

                    <label className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-mute-50">
                        <input
                            type="radio"
                            value="siteMax"
                            checked={flow === "siteMax"}
                            onChange={() => setFlow("siteMax")}
                        />
                        <div>
                            <div className="font-medium text-mute-800">{t("smartCharging.siteMaxAllTime")}</div>
                            <div className="text-xs text-mute-500">{t("smartCharging.siteMaxHelp")}</div>
                        </div>
                    </label>

                    <label className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-mute-50">
                        <input
                            type="radio"
                            value="daily"
                            checked={flow === "daily"}
                            onChange={() => setFlow("daily")}
                        />
                        <div>
                            <div className="font-medium text-mute-800">{t("smartCharging.dailySchedule")}</div>
                            <div className="text-xs text-mute-500">{t("smartCharging.dailyHelp")}</div>
                        </div>
                    </label>

                    <label className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-mute-50">
                        <input
                            type="radio"
                            value="weekly"
                            checked={flow === "weekly"}
                            onChange={() => setFlow("weekly")}
                        />
                        <div>
                            <div className="font-medium text-mute-800">{t("smartCharging.weeklySchedule")}</div>
                            <div className="text-xs text-mute-500">{t("smartCharging.weeklyHelp")}</div>
                        </div>
                    </label>

                    <div className="flex justify-end mt-6">
                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50"
                            disabled={!canGoNextFromType}
                            onClick={() => {
                                // siteMax -> go to Max power
                                // daily/weekly -> go straight to Validity
                                setStep(1);
                            }}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 1 – Max power (site max only) */}
            {isSiteMaxOnly && step === STEP_SITE_MAX && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("smartCharging.maxPower")}</h2>

                    <label className="text-sm font-medium text-mute-700">{t("form.maxPowerKw")}</label>
                    <input
                        type="number"
                        className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                        value={siteMaxKw}
                        onChange={(e) => setSiteMaxKw(e.target.value)}
                    />
                    <p className="text-mute-600 text-xs">{t("smartCharging.enterPowerOrEmpty")}</p>

                    <div className="flex justify-between">
                        <button className="btn btn-outline-primary px-4 py-2" onClick={prevStep}>
                            {t("common.back")}
                        </button>
                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50"
                            disabled={!canGoNextFromSite}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 1 – Validity (daily/weekly) */}
            {(isDaily || isWeekly) && step === STEP_VALIDITY && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("smartCharging.validity")}</h2>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-mute-700">{t("table.validFrom")}</label>
                            <input
                                type="date"
                                className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-mute-700">{t("table.validTo")}</label>
                            <input
                                type="date"
                                className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                    </div>

                    {dateOrderInvalid && (
                        <p className="text-red-600 text-sm">{t("smartCharging.validToAfterFrom")}</p>
                    )}

                    <div className="flex justify-between mt-6">
                        <button className="btn btn-outline-primary px-4 py-2" onClick={prevStep}>
                            {t("common.back")}
                        </button>

                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50"
                            disabled={!canGoNextFromValidity}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}

            {/* STEP – Days (weekly) */}
            {isWeekly && step === STEP_DAYS && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("smartCharging.days")}</h2>

                    <div className="space-y-2">
                        <div className="text-sm font-medium text-mute-700">{t("smartCharging.selectDays")}</div>

                        <div className="grid grid-cols-7 gap-2">
                            {WEEK_DAYS.map((d) => {
                                const active = recurrenceDays.includes(d.id);
                                return (
                                    <button
                                        key={d.id}
                                        type="button"
                                        onClick={() => toggleWeekDay(d.id)}
                                        className={
                                            "py-2 rounded-lg border text-sm font-semibold " +
                                            (active
                                                ? "bg-blue-600 text-white border-blue-600"
                                                : "bg-white text-mute-700 border-mute-300")
                                        }
                                    >
                                        {t(`weekdayShort.${d.id}`)}
                                    </button>
                                );
                            })}
                        </div>

                        {!weeklyValid && <p className="text-red-600 text-sm">{t("smartCharging.selectWeekday")}</p>}
                    </div>

                    <div className="flex justify-between mt-6">
                        <button className="btn btn-outline-primary px-4 py-2" onClick={prevStep}>
                            {t("common.back")}
                        </button>

                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50"
                            disabled={!weeklyValid}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}

            {/* TIME WINDOW (15 min steps) */}
            {(isDaily || isWeekly) && step === STEP_TIME && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("smartCharging.timeWindow")}</h2>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-mute-700">{t("form.startTime")}</label>
                            <input
                                type="time"
                                step="900"
                                className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                value={timeOfDayStart}
                                onChange={(e) => {
                                    setTimeOfDayStart(e.target.value);
                                }}
                                onBlur={() => {
                                    if (!timeOfDayStart) return;

                                    const [hh, mm] = timeOfDayStart.split(":").map(Number);
                                    if (Number.isNaN(hh) || Number.isNaN(mm)) return;

                                    let rounded = Math.round(mm / 15) * 15;
                                    let h = hh;

                                    if (rounded === 60) {
                                        rounded = 0;
                                        h = Math.min(23, h + 1);
                                    }

                                    const formatted =
                                        `${String(h).padStart(2, "0")}:${String(rounded).padStart(2, "0")}`;

                                    setTimeOfDayStart(formatted);
                                }}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-mute-700">{t("form.endTime")}</label>
                            <input
                                type="time"
                                step="900"
                                className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                                value={timeOfDayEnd}
                                onChange={(e) => {
                                    setTimeOfDayEnd(e.target.value);
                                }}
                                onBlur={() => {
                                    if (!timeOfDayEnd) return;

                                    const [hh, mm] = timeOfDayEnd.split(":").map(Number);
                                    if (Number.isNaN(hh) || Number.isNaN(mm)) return;

                                    let rounded = Math.round(mm / 15) * 15;
                                    let h = hh;

                                    if (rounded === 60) {
                                        rounded = 0;
                                        h = Math.min(23, h + 1);
                                    }

                                    const formatted =
                                        `${String(h).padStart(2, "0")}:${String(rounded).padStart(2, "0")}`;

                                    setTimeOfDayEnd(formatted);
                                }}
                            />
                        </div>
                    </div>
                    <p className="text-mute-600 text-xs">
                        {t("smartCharging.timeWindowHelp")}
                    </p>


                    <div className="flex justify-between mt-6">
                        <button className="btn btn-outline-primary px-4 py-2" onClick={prevStep}>
                            {t("common.back")}
                        </button>

                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50"
                            disabled={!canGoNextFromTimeWindow}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}

            {/* Schedule power (daily/weekly) */}
            {(isDaily || isWeekly) && step === STEP_SCHEDULE_POWER && (
                <div className="space-y-6">
                    <h2 className="text-xl font-bold ">{t("smartCharging.schedulePower")}</h2>

                    <label className="text-sm font-medium text-mute-700">{t("form.powerKw")}</label>
                    <input
                        type="number"
                        className="mt-1 w-full border-mute-300 rounded-lg focus:border-blue-500"
                        value={scheduleMaxKw}
                        onChange={(e) => setScheduleMaxKw(e.target.value)}
                    />

                    {scheduleInvalid && <p className="text-red-600 text-sm">{t("smartCharging.enterPowerGt0")}</p>}

                    <div className="flex justify-between mt-6">
                        <button className="btn btn-outline-primary px-4 py-2" onClick={prevStep}>
                            {t("common.back")}
                        </button>

                        <button
                            className="px-4 py-2 btn btn-primary rounded-lg disabled:opacity-50"
                            disabled={!canGoNextFromSchedulePower}
                            onClick={nextStep}
                        >
                            {t("common.next")} →
                        </button>
                    </div>
                </div>
            )}

            {/* SUMMARY */}
            {step === STEP_SUMMARY && (
                <div className="space-y-4">
                    <h2 className="text-xl font-bold ">{t("common.summary")}</h2>

                    <div className="bg-mute-50 p-4 rounded-xl text-sm text-mute-800 space-y-1">
                        <p>
                            <strong>{t("smartCharging.mode")}:</strong> {summaryRecurrence()}
                        </p>

                        <p>
                            <strong>{t("smartCharging.maxSchedulePower")}</strong>{" "}
                            {isSiteMaxOnly ? (siteMaxKw > 0 ? siteMaxKw : t("common.unlimited"))  : scheduleMaxKw} kW
                        </p>

                        {(isDaily || isWeekly) && (
                            <>
                                <p>
                                    <strong>{t("smartCharging.valid")}</strong> {startDate} – {endDate}
                                </p>
                                <p>
                                    <strong>{t("smartCharging.timeWindow")}:</strong> {timeOfDayStart} – {timeOfDayEnd}
                                </p>
                            </>
                        )}

                        {isSiteMaxOnly && (
                            <p className="text-mute-600">{t("smartCharging.appliesAllDay")}</p>
                        )}
                    </div>

                    <div className="flex justify-between mt-6">
                        <button className="btn btn-outline-primary px-4 py-2" onClick={prevStep}>
                            {t("common.back")}
                        </button>

                        <button
                            className="btn btn-primary px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-60"
                            disabled={isSubmitting}
                            onClick={handleSubmit}
                        >
                            {initialSchedule ? t("common.saveChanges") : t("common.createSchedule")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
